import {
  ImportValidationError,
  isIsoUtc,
  nowIso,
  type IdFactory,
} from "@/domain";
import {
  toStorageWriteError,
  type DatabaseInvalidationPublisher,
  type MarginaliaDatabase,
} from "@/storage";

import {
  createImportedDocument,
  createImportedReadingState,
  generateUniqueImportDocumentId,
  getDocumentImportContext,
  type DocumentImportCommitResult,
  type DocumentImportPreview,
} from "./import-document";
import {
  mergeAnnotations,
  type AnnotationMergeCounts,
} from "./merge-annotations";
import { migrateLibraryExportToCurrent } from "./migrations";
import { readJsonImportValue, type ReadJsonImportOptions } from "./read-import";
import {
  marginaliaLibraryExportV1Schema,
  type MarginaliaExportV1,
  type MarginaliaLibraryExportV1,
} from "./schemas/v1";

export type ParseLibraryImportOptions = ReadJsonImportOptions;

export type LibraryImportPreview = {
  documents: DocumentImportPreview[];
  documentCount: number;
  willCreateDocuments: number;
  pdfNeededDocuments: number;
  counts: AnnotationMergeCounts;
};

export type CommitLibraryImportOptions = {
  createId?: IdFactory;
  importedAt?: string;
  invalidation?: DatabaseInvalidationPublisher;
};

export type LibraryImportCommitResult = {
  documents: DocumentImportCommitResult[];
  createdDocuments: number;
  pdfNeededDocuments: number;
  counts: AnnotationMergeCounts;
};

export type ImportLibraryOptions = ParseLibraryImportOptions &
  CommitLibraryImportOptions & {
    confirm?: (preview: LibraryImportPreview) => boolean | Promise<boolean>;
  };

export type ImportLibraryResult =
  | {
      status: "imported";
      preview: LibraryImportPreview;
      result: LibraryImportCommitResult;
    }
  | {
      status: "cancelled";
      preview: LibraryImportPreview;
    };

function randomUuid(): string {
  if (globalThis.crypto?.randomUUID === undefined) {
    throw new Error("This browser does not provide crypto.randomUUID().");
  }

  return globalThis.crypto.randomUUID();
}

function emptyCounts(): AnnotationMergeCounts {
  return {
    created: 0,
    updated: 0,
    skipped: 0,
    conflicts: 0,
    deletions: 0,
  };
}

function addCounts(
  total: AnnotationMergeCounts,
  next: AnnotationMergeCounts,
): void {
  total.created += next.created;
  total.updated += next.updated;
  total.skipped += next.skipped;
  total.conflicts += next.conflicts;
  total.deletions += next.deletions;
}

function toDocumentBundle(
  library: MarginaliaLibraryExportV1,
  document: MarginaliaLibraryExportV1["documents"][number],
): MarginaliaExportV1 {
  return {
    format: "marginalia-annotations",
    formatVersion: 1,
    applicationVersion: library.applicationVersion,
    exportedAt: library.exportedAt,
    ...document,
  };
}

export function parseLibraryImportValue(
  value: unknown,
): MarginaliaLibraryExportV1 {
  return migrateLibraryExportToCurrent(value);
}

export async function parseLibraryImport(
  input: Blob | string | unknown,
  options: ParseLibraryImportOptions = {},
): Promise<MarginaliaLibraryExportV1> {
  return parseLibraryImportValue(await readJsonImportValue(input, options));
}

export async function previewLibraryImport(
  db: MarginaliaDatabase,
  input: MarginaliaLibraryExportV1,
  options: Pick<CommitLibraryImportOptions, "createId"> = {},
): Promise<LibraryImportPreview> {
  const library = marginaliaLibraryExportV1Schema.parse(input);
  const createId = options.createId ?? randomUuid;

  return db.transaction("r", [db.documents, db.annotations], async () => {
    const documents: DocumentImportPreview[] = [];
    const counts = emptyCounts();

    for (const libraryDocument of library.documents) {
      const bundle = toDocumentBundle(library, libraryDocument);
      const context = await getDocumentImportContext(db, bundle);
      const merge = mergeAnnotations(
        context.localAnnotations,
        bundle.annotations,
        context.documentId,
        {
          createId,
          occupiedIds: context.occupiedIds,
          foreignAnnotationIds: context.foreignAnnotationIds,
        },
      );
      const preview: DocumentImportPreview = {
        documentSha256: bundle.document.sha256,
        documentFileName: bundle.document.fileName,
        existingDocumentId: context.existingDocument?.id ?? null,
        willCreateDocument: context.existingDocument === undefined,
        pdfNeeded: context.existingDocument?.pdfAvailability !== "stored",
        counts: merge.counts,
      };
      documents.push(preview);
      addCounts(counts, preview.counts);
    }

    return {
      documents,
      documentCount: documents.length,
      willCreateDocuments: documents.filter(
        (document) => document.willCreateDocument,
      ).length,
      pdfNeededDocuments: documents.filter((document) => document.pdfNeeded)
        .length,
      counts,
    };
  });
}

export async function commitLibraryImport(
  db: MarginaliaDatabase,
  input: MarginaliaLibraryExportV1,
  options: CommitLibraryImportOptions = {},
): Promise<LibraryImportCommitResult> {
  const library = marginaliaLibraryExportV1Schema.parse(input);
  const createId = options.createId ?? randomUuid;
  const importedAt = options.importedAt ?? nowIso();
  if (!isIsoUtc(importedAt)) {
    throw new TypeError("Import timestamp must be canonical ISO 8601 UTC.");
  }

  let result: LibraryImportCommitResult;
  try {
    result = await db.transaction(
      "rw",
      [db.documents, db.annotations, db.readingStates],
      async () => {
        const documents: DocumentImportCommitResult[] = [];
        const counts = emptyCounts();

        for (const libraryDocument of library.documents) {
          const bundle = toDocumentBundle(library, libraryDocument);
          const context = await getDocumentImportContext(db, bundle);
          let documentId = context.existingDocument?.id;
          let createdDocument = false;

          if (documentId === undefined) {
            documentId = await generateUniqueImportDocumentId(db, createId);
            await db.documents.add(
              createImportedDocument(bundle, documentId, importedAt),
            );
            createdDocument = true;
          }

          const merge = mergeAnnotations(
            context.localAnnotations,
            bundle.annotations,
            documentId,
            {
              createId,
              occupiedIds: context.occupiedIds,
              foreignAnnotationIds: context.foreignAnnotationIds,
            },
          );
          if (merge.recordsToPut.length > 0) {
            await db.annotations.bulkPut(merge.recordsToPut);
          }

          const readingState = createImportedReadingState(
            documentId,
            bundle,
            importedAt,
          );
          if (readingState) {
            await db.readingStates.put(readingState);
          }

          const documentResult: DocumentImportCommitResult = {
            documentId,
            createdDocument,
            pdfNeeded:
              context.existingDocument?.pdfAvailability !== "stored",
            counts: merge.counts,
          };
          documents.push(documentResult);
          addCounts(counts, merge.counts);
        }

        return {
          documents,
          createdDocuments: documents.filter(
            (document) => document.createdDocument,
          ).length,
          pdfNeededDocuments: documents.filter(
            (document) => document.pdfNeeded,
          ).length,
          counts,
        };
      },
    );
  } catch (error) {
    if (error instanceof TypeError || error instanceof ImportValidationError) {
      throw error;
    }
    throw toStorageWriteError(error, "import a library backup");
  }

  for (const document of result.documents) {
    options.invalidation?.publish({
      type: "document-changed",
      documentId: document.documentId,
    });
  }
  return result;
}

export async function importLibrary(
  db: MarginaliaDatabase,
  input: Blob | string | unknown,
  options: ImportLibraryOptions = {},
): Promise<ImportLibraryResult> {
  const library = await parseLibraryImport(input, options);
  const preview = await previewLibraryImport(db, library, options);

  if (options.confirm && !(await options.confirm(preview))) {
    return { status: "cancelled", preview };
  }

  const result = await commitLibraryImport(db, library, options);
  return { status: "imported", preview, result };
}
